import { useEffect, useState } from "react";
import { getAttachmentPreview, type AttachmentPreviewKind } from "../data/attachmentPreviews";
import "../styles/attachments.css";

interface AttachmentListProps {
  attachments: string[];
}

const kindLabels: Record<AttachmentPreviewKind, string> = {
  pdf: "PDF",
  spreadsheet: "表計算",
  text: "テキスト",
  archive: "圧縮ファイル",
  unknown: "ファイル"
};

export function AttachmentList({ attachments }: AttachmentListProps) {
  const [selectedAttachment, setSelectedAttachment] = useState<string>();
  const preview = selectedAttachment ? getAttachmentPreview(selectedAttachment) : undefined;

  useEffect(() => {
    if (selectedAttachment && !attachments.includes(selectedAttachment)) {
      setSelectedAttachment(undefined);
    }
  }, [attachments, selectedAttachment]);

  useEffect(() => {
    if (!selectedAttachment) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedAttachment(undefined);
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedAttachment]);

  if (attachments.length === 0) return null;

  return (
    <>
      <div className="attachments">
        <div className="attachments-title">添付ファイル ({attachments.length})</div>
        <div className="attachment-grid">
          {attachments.map((attachment) => (
            <button
              type="button"
              className="attachment-chip"
              key={attachment}
              title="クリックして社内ビューアーで開く"
              aria-haspopup="dialog"
              onClick={() => setSelectedAttachment(attachment)}
            >
              <span className="attachment-icon" aria-hidden="true">▧</span>
              <span>{attachment}</span>
            </button>
          ))}
        </div>
      </div>

      {selectedAttachment && preview && (
        <div
          className="attachment-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelectedAttachment(undefined);
          }}
        >
          <section
            className="attachment-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="attachment-preview-title"
          >
            <header className="attachment-modal-header">
              <div className="attachment-modal-heading">
                <div className="attachment-modal-kicker">社内セキュアビューアー</div>
                <h2 id="attachment-preview-title">{selectedAttachment}</h2>
              </div>
              <button
                type="button"
                className="attachment-modal-close"
                aria-label="添付ファイルを閉じる"
                autoFocus
                onClick={() => setSelectedAttachment(undefined)}
              >
                ×
              </button>
            </header>

            <div className="attachment-modal-toolbar">
              <span className="attachment-type-badge">{kindLabels[preview.kind]}</span>
              <span>読み取り専用・社外転送禁止</span>
            </div>

            <div className="attachment-preview-scroll">
              <article className={`attachment-preview-sheet is-${preview.kind}`}>
                <h3>{preview.title}</h3>
                <div className="attachment-preview-meta">
                  {preview.metadata.map(([label, value]) => (
                    <div key={`${label}-${value}`}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
                <div className="attachment-preview-lines">
                  {preview.lines.map((line, index) => (
                    <p className="attachment-preview-line" key={`${line}-${index}`}>{line}</p>
                  ))}
                </div>
                {preview.warning && <p className="attachment-preview-warning">注意：{preview.warning}</p>}
              </article>
            </div>

            <footer className="attachment-modal-footer">
              <button type="button" onClick={() => setSelectedAttachment(undefined)}>閉じる</button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
}
